import { z } from "zod";

import { createFileRoute } from "@tanstack/react-router";

import type { RouterContextType } from "@/api/permissions/permissions.api";
import { ChatContainer } from "@/components/features/Chat/ChatContainer";
import SubjectChatPanel from "@/components/features/Chat/SubjectChatPanel";
import { authGuardWithPermissions } from "@/utils/authGuard";
import styles from "./mobileChat.module.scss";

const mobileChatSearchSchema = z.object({
  workOrderId: z.string().optional(),
});

export const Route = createFileRoute("/_authenticated/mobile/chat/")({
  component: MobileChat,
  validateSearch: mobileChatSearchSchema.parse,
  beforeLoad: async (args) => {
    await authGuardWithPermissions({
      context: args.context as RouterContextType,
      location: args.location as { pathname: string },
    });
  },
});

/**
 * The mobile Chat tab.
 *
 * A service provider reaches this two ways: the bottom-nav Chat tab (no
 * `workOrderId`, the general inbox below) and a work order's own chat entry
 * point (`workOrderId` set). The latter renders the same work-order-scoped
 * panel the desktop app's WOChatTab uses, rather than the general inbox --
 * a provider chats about a work order, not by picking a contact out of the
 * cross-tenant directory the general inbox's "+" offers. The general inbox
 * itself is the same shared `ChatContainer` the desktop `/chat` route uses,
 * not a second copy of its ~400 lines of hooks/mutations/handlers.
 */
function MobileChat() {
  const { workOrderId } = Route.useSearch();

  if (workOrderId) {
    return (
      <SubjectChatPanel
        subjectType="work_order"
        subjectId={workOrderId}
        autoCreateDefault
        className={styles.mobileWoChatHost}
      />
    );
  }

  return <ChatContainer className={styles.mobileChatRoot} />;
}
